import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup, {
  ToolPrepCloseContext,
} from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import ToolPrepFlatResultStage from "../../../shared/Popup/ToolPrepFlatResultStage";
import BackgroundColorPreview from "./BackgroundColorPreview";
import BackgroundControlColorMenu from "../../../../editor/panels/background/BackgroundControlColorMenu";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import ToolPrepPreviewBgCards from "../../../shared/previewBg/ToolPrepPreviewBgCards";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { useToolPrepBakedApplyFlow } from "../../../hooks/useToolPrepBakedApplyFlow";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { exportBackgroundFillPrepImageBlob } from "../../../lib/exportToolPrepEffectToPngBlob";
import { hasActiveBackgroundFillSettings } from "../../../lib/toolPrepEffectActiveSettings";
import {
  TOOL_PREP_PREVIEW_BG,
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
  initialToolPrepPreviewBg,
} from "../../../../../utils/editorEntrySession";
import {
  TOOL_PREP_CUTOUT_REVEAL_MS,
  useDelayedImageReady,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import "./BackgroundColorToolPrep.css";

export const BACKGROUND_COLOR_ENTRY_DEFAULT = "#FFFFFF";

const BG_COLOR_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete: "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  previewAlt: "Background color image download",
  primaryActionLabel: "Add new image",
};

/**
 * Editing Tools → Background color prep: auto BG removal, Color/Transparent/Original
 * preview cards, and shared Color palette menu.
 */
const BackgroundColorToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const openedFromEditor = useContext(ToolPrepCloseContext) === "editor";
  const [selectedColor, setSelectedColor] = useState(
    BACKGROUND_COLOR_ENTRY_DEFAULT
  );
  const [previewBg, setPreviewBg] = useState(() =>
    initialToolPrepPreviewBg(TOOL_PREP_PREVIEW_BG.COLOR)
  );

  const [cutoutUrl, setCutoutUrl] = useState(
    () =>
      (cachedCutoutUrl &&
        (cachedCutoutUrl.startsWith("blob:") ||
          cachedCutoutUrl.startsWith("data:")) &&
        cachedCutoutUrl) ||
      null
  );
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const bgStartedRef = useRef(false);
  const subjectPlacementRef = useRef(null);
  const onCutoutReadyRef = useRef(onCutoutReady);
  const scrollBodyRef = useRef(null);
  const resetAppliedRef = useRef(() => {});
  onCutoutReadyRef.current = onCutoutReady;

  const {
    sourceImageUrl,
    resolvedFile,
    sourceFileName,
    sourceFileSizeLabel,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      setBgError(null);
      bgStartedRef.current = false;
      resetAppliedRef.current();
      subjectPlacementRef.current = null;
      setSelectedColor(BACKGROUND_COLOR_ENTRY_DEFAULT);
      setPreviewBg(initialToolPrepPreviewBg(TOOL_PREP_PREVIEW_BG.COLOR));
    },
  });

  const {
    applied: fillApplied,
    busy: fillBusy,
    resultUrl: fillPreviewUrl,
    applyError: fillApplyError,
    setApplyError: setFillApplyError,
    apply: applyFill,
    resetApplied,
    downloadBusy,
    handleDownload,
    downloadFlow,
  } = useToolPrepBakedApplyFlow({
    sourceFileName,
    fallbackDownloadName: "background.png",
    toolKey: "background_color",
    shareTitle: "Background color tool",
    shareText:
      "I added a background color to my photo with LazyStudio's Background Color tool!",
    thankYouCopy: BG_COLOR_THANK_YOU_COPY,
  });

  resetAppliedRef.current = resetApplied;

  useEffect(() => {
    if (
      cachedCutoutUrl &&
      (cachedCutoutUrl.startsWith("blob:") || cachedCutoutUrl.startsWith("data:"))
    ) {
      setCutoutUrl(cachedCutoutUrl);
    }
  }, [cachedCutoutUrl]);

  useEffect(() => {
    if (bgStartedRef.current) return;
    if (!resolvedFile && !sourceImageUrl) return;

    if (
      cutoutUrl?.startsWith("blob:") ||
      cutoutUrl?.startsWith("data:") ||
      cachedCutoutUrl?.startsWith("blob:") ||
      cachedCutoutUrl?.startsWith("data:")
    ) {
      bgStartedRef.current = true;
      if (!cutoutUrl && cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
      return;
    }

    bgStartedRef.current = true;

    (async () => {
      setBgLoading(true);
      setBgError(null);
      try {
        const token = await getToken();
        const { cutoutUrl: nextUrl, creationId: cid } =
          await ensureBackgroundRemoved({
            token,
            imageFile: resolvedFile,
            imageUrl: sourceImageUrl,
            cachedCutoutUrl: null,
          });
        setCutoutUrl(nextUrl);
        if (cid != null) setCreationId(cid);
        onCutoutReadyRef.current?.(nextUrl);
      } catch (err) {
        setBgError(err?.message || "Failed to remove background");
        bgStartedRef.current = false;
      } finally {
        setBgLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedFile, sourceImageUrl, getToken]);

  const previewImageUrl = cutoutUrl || null;
  const cutoutRevealReady = useDelayedImageReady(
    previewImageUrl,
    TOOL_PREP_CUTOUT_REVEAL_MS
  );
  const revealCutoutUrl = cutoutRevealReady ? previewImageUrl : null;
  const frameNaturalSize = useImageNaturalSize(revealCutoutUrl);

  const resolveAppliedColor = () =>
    previewBg === TOOL_PREP_PREVIEW_BG.COLOR ||
    previewBg === TOOL_PREP_PREVIEW_BG.WHITE
      ? selectedColor || BACKGROUND_COLOR_ENTRY_DEFAULT
      : null;

  const handleApplyBackground = useCallback(
    (color) => {
      if (!color) return;
      resetApplied();
      setFillApplyError(null);
      setSelectedColor(color);
      setPreviewBg(TOOL_PREP_PREVIEW_BG.COLOR);
    },
    [resetApplied, setFillApplyError]
  );

  const handlePreviewBgChange = (nextBg) => {
    if (fillApplied) return;
    resetApplied();
    setFillApplyError(null);
    setPreviewBg(nextBg);
  };

  const buildExport = useCallback(() => {
    const url = revealCutoutUrl;
    const backgroundColor = resolveAppliedColor();
    return () =>
      exportBackgroundFillPrepImageBlob(url, {
        previewBg,
        backgroundColor,
        uploadedImageUrl: sourceImageUrl,
      });
  }, [previewBg, revealCutoutUrl, selectedColor, sourceImageUrl]);

  const handleApplyFill = async () => {
    if (
      !revealCutoutUrl ||
      fillBusy ||
      !hasActiveBackgroundFillSettings(previewBg)
    ) {
      return;
    }
    await applyFill(buildExport());
  };

  const handleApplyToEditor = () => {
    if (!revealCutoutUrl || !hasActiveBackgroundFillSettings(previewBg)) return;
    onDone({
      imageUrl: revealCutoutUrl,
      creationId,
      previewBg,
      backgroundColor: resolveAppliedColor(),
      entryFlow: "background_color",
    });
  };

  const handleAdjustFill = () => {
    setFillApplyError(null);
    resetApplied();
  };

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      if (fillApplied && (cutoutUrl || revealCutoutUrl || sourceImageUrl)) {
        const solid = resolveAppliedColor();
        onDone({
          imageUrl:
            previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL
              ? sourceImageUrl || cutoutUrl
              : cutoutUrl || revealCutoutUrl,
          creationId,
          previewBg,
          backgroundColor: solid,
          entryFlow: "background_color",
        });
        return;
      }

      let imageUrl = cutoutUrl;
      let doneCreationId = creationId;
      if (
        !imageUrl ||
        (/^https?:\/\//i.test(imageUrl) && !imageUrl.startsWith("blob:"))
      ) {
        const token = await getToken();
        const { cutoutUrl: nextUrl, creationId: cid } =
          await ensureBackgroundRemoved({
            token,
            imageFile: resolvedFile,
            imageUrl: sourceImageUrl,
            cachedCutoutUrl: cutoutUrl,
          });
        imageUrl = nextUrl;
        doneCreationId = cid ?? doneCreationId;
        setCutoutUrl(nextUrl);
        if (cid != null) setCreationId(cid);
        onCutoutReadyRef.current?.(nextUrl);
      }
      if (!imageUrl) {
        throw new Error("Background-removed image is not ready");
      }
      onDone({
        imageUrl,
        creationId: doneCreationId,
        previewBg,
        backgroundColor: resolveAppliedColor(),
        entryFlow: "background_color",
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish background color setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const canApplyFill =
    Boolean(sourceImageUrl) &&
    Boolean(revealCutoutUrl) &&
    hasActiveBackgroundFillSettings(previewBg) &&
    !fillBusy;

  const configureFooterHint = !sourceImageUrl
    ? "Upload an image to start adding a background color."
    : !revealCutoutUrl
      ? "Removing background…"
      : !hasActiveBackgroundFillSettings(previewBg)
        ? "Choose a color or the Preview card, then click Apply Background."
        : "Pick a color, then click Apply Background.";

  const leftContent = (
    <div className="bctp-panel">
      <div className="bctp-preview-bg-block">
        <ToolPrepPreviewBgCards
          previewBg={previewBg}
          onPreviewBgChange={handlePreviewBgChange}
          cutoutUrl={revealCutoutUrl}
          uploadedImageUrl={sourceImageUrl}
          cutoutPending={Boolean(sourceImageUrl) && !cutoutRevealReady}
          toolCardMode={TOOL_PREP_PREVIEW_BG.COLOR}
          toolCardColor={selectedColor}
          toolCardLabel="Preview"
          onUploadClick={openUploadPicker}
          onRemove={clearSource}
          uploadProgress={uploadProgress}
          sourceName={sourceFileName}
          sourceSizeLabel={sourceFileSizeLabel}
          actionsDisabled={bgLoading || doneBusy || downloadBusy}
          removeBgControls={{
            active: true,
            locked: true,
            bio: "Required — so the color fills behind the subject, not over the original photo.",
            error: bgError,
          }}
        />
      </div>

      <div
        className={`bctp-color-controls tool-prep-scroll${
          fillApplied ? " bctp-color-controls--locked" : ""
        }`}
        ref={scrollBodyRef}
      >
        <BackgroundControlColorMenu
          currentBgColor={selectedColor}
          onColorSelect={handleApplyBackground}
          onApplyBackground={handleApplyBackground}
        />
      </div>
    </div>
  );

  useSyncScrollbarWidth(scrollBodyRef, [
    sourceImageUrl,
    cutoutUrl,
    fillApplied,
    selectedColor,
  ]);

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey="background_color"
        title="Background color"
        left={leftContent}
        preview={
          fillApplied ? (
            <ToolPrepFlatResultStage
              className="bctp-preview"
              resultImageUrl={fillPreviewUrl}
              previewBg={previewBg}
              stageColor={resolveAppliedColor()}
              applying={fillBusy}
              applyingLabel="Applying background…"
              resultAlt="Background color result"
              frameNaturalSize={frameNaturalSize}
              subjectPlacement={subjectPlacementRef.current}
              softWatermarkToolKey="background_color"
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          ) : (
            <BackgroundColorPreview
              imageUrl={revealCutoutUrl}
              uploadedImageUrl={sourceImageUrl}
              loading={Boolean(sourceImageUrl) && !cutoutRevealReady}
              applying={fillBusy}
              applyingLabel="Applying background…"
              previewBg={previewBg}
              selectedColor={selectedColor}
              showSubjectBox={Boolean(revealCutoutUrl)}
              onSubjectPlacement={(placement) => {
                subjectPlacementRef.current = placement;
              }}
              emptyLabel={
                uploadProgress != null && !sourceImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          )
        }
        onDone={
          openedFromEditor
            ? handleApplyToEditor
            : fillApplied
              ? handleDone
              : handleApplyFill
        }
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={
          openedFromEditor
            ? null
            : fillApplied
              ? () => handleDownload(buildExport())
              : null
        }
        onOpenInEditor={openedFromEditor ? null : fillApplied ? handleDone : null}
        onSecondaryAction={
          openedFromEditor ? null : fillApplied ? handleAdjustFill : null
        }
        secondaryActionLabel="Adjust background"
        busy={
          openedFromEditor
            ? Boolean(bgLoading && !cutoutUrl)
            : fillBusy || doneBusy || (bgLoading && !cutoutUrl) || downloadBusy
        }
        busyLabel="Applying background…"
        showDone={openedFromEditor || !fillApplied}
        doneDisabled={!canApplyFill}
        doneLabel="Apply Background"
        footerActionOrder={
          fillApplied
            ? ["cancel", "secondary", "download", "openInEditor"]
            : ["cancel", "primary"]
        }
        openInEditorLabel="Open in Editor"
        footerHint={
          fillApplyError ||
          (fillApplied ? TOOL_PREP_RESULT_FOOTER_HINT : configureFooterHint)
        }
        footerHintClassName={
          fillApplyError ? "tool-prep-popup__footer-hint--error" : ""
        }
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="background_color"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default BackgroundColorToolPrep;
